import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, User as UserIcon, Terminal, AlertCircle } from 'lucide-react';
import type { ChatMessage } from '../hooks/useDebugSession';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatToolOutput(output: unknown): string {
  if (output === null || output === undefined) return '';
  if (typeof output === 'string') return output;
  try {
    return JSON.stringify(output, null, 2);
  } catch {
    return String(output);
  }
}

export function MessageBubble({ message }: { message: ChatMessage }) {
  if (message.role === 'system') {
    return (
      <div className="flex items-center justify-center my-2">
        <span className="text-xs text-text-muted px-3 py-1 rounded-full bg-bg-card border border-border">
          <AlertCircle size={11} className="inline mr-1" />
          {message.content}
        </span>
      </div>
    );
  }

  if (message.role === 'tool') {
    const inputPreview = message.toolInput
      ? JSON.stringify(message.toolInput).slice(0, 200)
      : '';
    return (
      <div className="my-2 ml-10">
        <div
          className={`card border-l-2 ${
            message.toolError ? 'border-l-red-500' : 'border-l-purple-500'
          } py-2 px-3`}
        >
          <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
            <Terminal size={12} />
            <span className="font-mono font-medium text-purple-700">
              {message.toolName}
            </span>
            <span className="ml-auto">{formatTime(message.ts)}</span>
          </div>
          {inputPreview && (
            <pre className="text-xs font-mono text-text-muted overflow-x-auto whitespace-pre-wrap break-all">
              {inputPreview}
              {inputPreview.length >= 200 && '…'}
            </pre>
          )}
          {message.toolOutput !== undefined && (
            <details className="mt-1">
              <summary className="text-xs text-text-muted cursor-pointer hover:text-text">
                resultado
              </summary>
              <pre className="mt-1 text-xs font-mono text-text bg-bg p-2 rounded overflow-x-auto max-h-60 whitespace-pre-wrap break-all">
                {formatToolOutput(message.toolOutput).slice(0, 4000)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-2 my-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
          isUser ? 'bg-blue-600' : 'bg-bg-hover border border-border'
        }`}
      >
        {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
      </div>
      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div
          className={`inline-block text-left rounded-lg px-3 py-2 ${
            isUser ? 'bg-blue-600 text-white' : 'bg-bg-card border border-border'
          }`}
        >
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap break-words">
              {message.content}
            </div>
          ) : (
            <div className="markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content || (message.isStreaming ? '▍' : '')}
              </ReactMarkdown>
              {message.isStreaming && message.content && (
                <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-0.5" />
              )}
            </div>
          )}
        </div>
        <div className={`text-xs text-text-muted mt-1 ${isUser ? 'text-right' : ''}`}>
          {formatTime(message.ts)}
        </div>
      </div>
    </div>
  );
}
